import { useCallback, useMemo } from 'react';
import {
  EditorGraphInput,
  EditorLocalStreamingState,
  EditorHandleProps,
} from 'src/types';
import { get_access_token } from 'src/utils';
import { setEditorStreaming, useAppDispatch } from 'src/store';
import { useGetEditorThread } from './useGetEditorThread';
import { useErrorToast } from '../toastHooks';
import { useRunEditorHelpers } from './useRunEditorHelpers';
import useSafeLogger from 'src/hooks/useSafeLogger';

export const useRunEditorStream = () => {
  const dispatch = useAppDispatch();
  const { pgLogError } = useSafeLogger();

  const { onShowErrorToast } = useErrorToast();
  const { editorThread, artifactOutput } = useGetEditorThread();

  const { thread_id } = editorThread ?? {};

  const {
    dispatchUserMessage,
    fetchStreamReader,
    onChainStart,
    onChatModelStream,
    onChatModelEnd,
    onChainEnd,
  } = useRunEditorHelpers();

  const initLocalState = useCallback(
    (): EditorLocalStreamingState => ({
      generateArtifactToolCallStr: '',
      fullNewArtifactContent: '',
    }),
    [],
  );

  const onRunEditorStream = useCallback(
    async (input: EditorGraphInput) => {
      if (!thread_id) {
        return;
      }

      const { userMessage, highlightedCode } = input;

      if (!userMessage) {
        return;
      }

      dispatchUserMessage(userMessage);

      const token = await get_access_token();

      if (!token) {
        pgLogError('Editor: missing access token', {});
        throw new Error('Auth token must not be empty');
      }

      dispatch(setEditorStreaming(true));

      const localState = initLocalState();

      const eventHandlers: Record<string, (props: EditorHandleProps) => void> =
        {
          on_chain_start: onChainStart,
          on_chat_model_stream: onChatModelStream,
          on_chat_model_end: onChatModelEnd,
          on_chain_end: onChainEnd,
        };

      try {
        const reader = await fetchStreamReader({ input, token });
        if (!reader) {
          return;
        }

        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          if (!value) {
            continue;
          }

          const chunkText = decoder.decode(value, { stream: true });
          buffer += chunkText;

          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || line.startsWith('event:')) {
              continue;
            }

            if (line.startsWith('data:')) {
              const jsonStr = line.slice('data:'.length).trim();

              try {
                const parsedData = JSON.parse(jsonStr);
                const event = parsedData?.event || '';

                const handler = eventHandlers[event];
                if (handler) {
                  handler({
                    parsedData,
                    localState,
                    currentArtifactOutput: artifactOutput,
                    highlightedCode,
                  });
                }
              } catch (_) {
                onShowErrorToast();
              }
            }
          }
        }
      } catch (_) {
        onShowErrorToast();
      } finally {
        dispatch(setEditorStreaming(false));
      }
    },
    [
      thread_id,
      artifactOutput,
      dispatch,
      onShowErrorToast,
      dispatchUserMessage,
      fetchStreamReader,
      initLocalState,
      onChainStart,
      onChatModelStream,
      onChatModelEnd,
      onChainEnd,
      pgLogError,
    ],
  );

  return useMemo(
    () => ({
      onRunEditorStream,
    }),
    [onRunEditorStream],
  );
};
